import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="rgu-container py-20 grid grid-cols-1 md:grid-cols-12 gap-12 max-w-4xl">
        <div className="md:col-span-5">
          <div className="rgu-eyebrow text-slate-500 mb-3">Account · Recovery</div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0A192F]">Forgot your password?</h1>
          <p className="mt-4 text-slate-600">
            Enter the email associated with your RGU account and we&apos;ll send you a secure link to set a new password. The link expires in 1 hour and can only be used once.
          </p>
        </div>

        {sent ? (
          <div className="md:col-span-7 border border-slate-200 p-8" data-testid="forgot-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg text-[#0A192F]">Check your inbox</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                  If an account exists for <span className="font-mono-rgu text-[#0A192F]">{email}</span>, we&apos;ve sent reset instructions to that address. Please check your email (including the spam folder) within the next few minutes.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    onClick={() => { setSent(false); setEmail(""); }}
                    className="text-xs font-mono-rgu uppercase tracking-widest text-slate-500 hover:text-[#0A192F] underline"
                    data-testid="forgot-resend-btn"
                  >
                    Use a different email
                  </button>
                  <Link to="/login" className="text-xs font-mono-rgu uppercase tracking-widest text-[#0A192F] hover:underline" data-testid="forgot-back-to-login">
                    Back to sign in
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="md:col-span-7 border border-slate-200 p-8 space-y-5" data-testid="forgot-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="forgot-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none"
                placeholder="you@example.com"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="rgu-btn-primary w-full justify-center text-xs uppercase tracking-widest inline-flex items-center gap-2"
              data-testid="forgot-submit"
            >
              <Mail className="w-4 h-4" />
              {loading ? "Sending…" : "Send Reset Link"}
            </button>
            <div className="text-sm text-slate-500 pt-2">
              Remember your password?{" "}
              <Link to="/login" className="text-[#0A192F] underline" data-testid="forgot-signin-link">
                Sign in
              </Link>
            </div>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
